import { RestError } from '@azure/core-http';
import { UserDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useQuerySearchOccupants } from '../../../../users/users.queries';
import { useQueryGetDeliveryContainer, useQueryGetHub } from '../../../hubs.queries';
import { useMutationReserveContainerForParty } from '../../../lockers.queries';

export interface ReserveContainerActionProps {
  deliveryContainerID: string;
  projectID: string;
  onAbort(): void;
  onSuccess(): void;
}

const SEARCH_DEBOUNCE_MS = 300;

export function useReserveContainerAction({ deliveryContainerID, onAbort, onSuccess }: ReserveContainerActionProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [error, setError] = useState<RestError | undefined>(undefined);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const container = useQueryGetDeliveryContainer(deliveryContainerID);
  const hub = useQueryGetHub(container.data?.hub.id);
  const projectIDs = useMemo(() => hub.data?.acceptsDeliveriesForProjectIDs ?? [], [hub.data]);

  const occupants = useQuerySearchOccupants({
    searchTerm: debouncedSearchTerm,
    projectIDs,
    enabled: projectIDs.length > 0
  });

  const reserveMutation = useMutationReserveContainerForParty();

  const handlers = {
    onSearchChange(value: string) {
      setSearchTerm(value);
    },
    onSelectUser(user: UserDto | null) {
      setSelectedUser(user);
    },
    onCancelIntent() {
      onAbort();
    },
    onSubmit() {
      if (!selectedUser) {
        return;
      }
      setError(undefined);
      reserveMutation.mutate(
        { deliveryContainerID, partyIDs: [selectedUser.id] },
        {
          onSuccess() {
            onSuccess();
          },
          onError(err: RestError) {
            setError(err);
          }
        }
      );
    }
  };

  return {
    container,
    hub,
    occupants,
    selectedUser,
    error,
    submitting: reserveMutation.isLoading,
    handlers
  } as const;
}
